/**
 * PostHog initialiser. Gracefully no-ops when env vars are missing so the
 * app keeps working without a key. Drop your PROJECT API KEY (phc_...) into
 * frontend/.env as REACT_APP_POSTHOG_KEY to activate. Optional:
 *   REACT_APP_POSTHOG_HOST (defaults to https://us.i.posthog.com)
 *   REACT_APP_POSTHOG_DEBUG (set to "true" to enable debug logging)
 *
 * Exposes:
 *   - getPosthog()  → the client (or null)
 *   - capture(event, properties)  → safe capture helper (silent on no client)
 *   - identifyUser(user) / resetIdentity()
 */
import posthog from "posthog-js";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
const IS_PROD = process.env.NODE_ENV === "production";
const DEBUG = process.env.REACT_APP_POSTHOG_DEBUG === "true";

let initialised = false;
let lastIdentifiedId = null;

export function initPosthog() {
  if (initialised || typeof window === "undefined") return;
  if (!KEY) return;          // no key → silent no-op (intentional)
  try {
    posthog.init(KEY, {
      api_host: HOST,
      capture_pageview: "history_change", // SPA-friendly auto pageviews
      autocapture: IS_PROD,                // off in dev / beta unless prod
      opt_out_capturing_by_default: !IS_PROD,
      persistence: "localStorage+cookie",
      debug: DEBUG,
    });
    initialised = true;
  } catch (e) {
    // Never break the app for analytics.
     
    console.warn("PostHog init failed", e);
  }
}

export function getPosthog() {
  return initialised ? posthog : null;
}

export function capture(event, properties = {}) {
  const ph = getPosthog();
  if (!ph) return;
  try {
    ph.capture(event, properties);
  } catch { /* swallow */ }
}

export function identifyUser(user) {
  const ph = getPosthog();
  if (!ph || !user?.id) return;
  if (lastIdentifiedId === user.id) return;
  try {
    ph.identify(user.id, {
      email: user.email,
      name: user.name,
      is_admin: !!user.is_admin,
    });
    if (!IS_PROD) ph.opt_in_capturing();
    lastIdentifiedId = user.id;
  } catch { /* swallow */ }
}

export function resetIdentity() {
  const ph = getPosthog();
  if (!ph) return;
  try { ph.reset(); } catch { /* swallow */ }
  lastIdentifiedId = null;
}
